<script setup lang="ts">
import { createConsumer, type Consumer, type Subscription } from '@rails/actioncable'
import { onMounted, onUnmounted, ref } from 'vue'

interface Item { id: number; title: string; body: string; action_url: string | null; read_at: string | null }
const props = defineProps<{ endpoint: string; historyUrl: string; label: string }>()
const items = ref<Item[]>([])
const unread = ref(0)
let consumer: Consumer | null = null
let subscription: Subscription | null = null
const receivedItems = new Map<number, Item>()
let loadSequence = 0

async function load(): Promise<void> {
  const sequence = ++loadSequence
  try {
    const response = await fetch(props.endpoint, { headers: { Accept: 'application/json' } })
    if (!response.ok) return
    const payload = await response.json() as { notifications: Item[]; unread_count: number }
    if (sequence !== loadSequence) return
    const serverIds = new Set(payload.notifications.map((item) => item.id))
    const missingLiveItems = [...receivedItems.values()].filter((item) => !serverIds.has(item.id))
    items.value = [...missingLiveItems, ...payload.notifications]
      .filter((item, index, all) => all.findIndex((candidate) => candidate.id === item.id) === index)
      .slice(0, 8)
    unread.value = payload.unread_count + missingLiveItems.filter((item) => !item.read_at).length
    payload.notifications.forEach((item) => receivedItems.delete(item.id))
  } catch {}
}

function receive(item: Item): void {
  receivedItems.set(item.id, item)
  const existing = items.value.findIndex((candidate) => candidate.id === item.id)
  if (existing >= 0) items.value.splice(existing, 1)
  else if (!item.read_at) unread.value += 1
  items.value.unshift(item)
  items.value = items.value.slice(0, 8)
}

onMounted(() => {
  consumer = createConsumer('/cable?as=player')
  subscription = consumer.subscriptions.create({ channel: 'NotificationsChannel', as: 'player' }, {
    connected: () => { void load() },
    received: receive,
  })
})

onUnmounted(() => { subscription?.unsubscribe(); consumer?.disconnect() })

async function open(item: Item): Promise<void> {
  const url = new URL(props.endpoint, window.location.origin)
  url.pathname = `${url.pathname.replace(/\.json$/, '')}/${item.id}/read`
  await fetch(url, { method: 'PATCH', headers: { 'X-CSRF-Token': document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '' } })
  window.location.assign(item.action_url || props.historyUrl)
}
</script>

<template>
  <details class="dropdown dropdown-end">
    <summary class="btn btn-ghost btn-sm relative" :aria-label="label">
      <span aria-hidden="true">🔔</span><span v-if="unread" class="badge badge-primary badge-xs">{{ unread > 9 ? '9+' : unread }}</span>
    </summary>
    <div class="dropdown-content z-50 mt-2 w-80 rounded-box border border-base-300 bg-base-100 p-3 shadow-xl">
      <a v-for="item in items" :key="item.id" :href="item.action_url || historyUrl" class="block rounded-lg p-2 hover:bg-base-200" @click.prevent="open(item)">
        <strong class="block">{{ item.title }}</strong><small>{{ item.body }}</small>
      </a>
      <p v-if="!items.length" class="p-3 text-sm opacity-60">—</p>
      <a :href="historyUrl" class="btn btn-ghost btn-sm mt-2 w-full">{{ label }}</a>
    </div>
  </details>
</template>
